import React, { useEffect, useState } from 'react';
import { Flex } from '@/ui/Flex/Flex';
import { Button } from '@/ui/Button/Button';
import {
  IconEdit,
  IconPlay,
  IconDuplicate,
  IconTrash,
  IconPreview,
} from '@/icons';
import { Tooltip } from '@/ui/Tooltip/Tooltip';
import { KebapMenu, MenuItem } from '@/ui/KebapMenu/KebapMenu';
import { useNavigate } from 'react-router-dom';
import { useToast, TOASTTYPE, DURATIONS } from '@/ui/Toast/Toast';
import { useDispatchStartPlayer } from '@/utils/useDispatchStartPlayer';
import { useIntl, FormattedMessage } from 'react-intl';
import { T } from '@/ui/Text/Text';
import {
  StyledButtonGroup,
  StyledConfig,
  StyledDivider,
  StyledStatsContainer,
  StyledSubText,
} from './styles';
import formatDateTime from '../../utils/convertIsoToDate';
import { envIsElectron } from '../../../const/envCheck';
import { TransformedSubsidiary } from '../../utils/config';
import { useUnitData } from '../_data/useUnitData';
import { useOfflineAvailability } from '../_data/useOfflineAvailability';
import { DisplayStatus } from './DisplayStatus/DisplayStatus';

interface Props {
  item: TransformedSubsidiary;
  index: number;
  isAdmin: boolean;
  setSelectedUnitId: React.Dispatch<React.SetStateAction<string | null>>;
  setIsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDuplicateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDeleteConfirmationModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}
const ConfigCard = ({
  item,
  index,
  isAdmin,
  setSelectedUnitId,
  setIsEditModalOpen,
  setDuplicateModalOpen,
  setIsDeleteConfirmationModalOpen,
}: Props) => {
  const toast = useToast();
  const intl = useIntl();
  const dispatchStartPlayer = useDispatchStartPlayer();
  const navigate = useNavigate();
  const { data: unit } = useUnitData(item.unitId);
  const { checkOfflineAvailability } = useOfflineAvailability();
  const [isReadyForOffline, setIsReadyForOffline] = useState(false);

  useEffect(() => {
    if (!unit) return;
    if (!envIsElectron) return;
    checkOfflineAvailability(unit).then((result) => {
      setIsReadyForOffline(result);
    });
  }, [item.unitId, unit, checkOfflineAvailability, navigator.onLine]);

  const DisplayStatusProps = {
    isElectron: envIsElectron,
    totalPlayers: item.playerTotals.total,
    totalOnline: item.playerTotals.online,
    isReady: isReadyForOffline,
    productType: item.productType ? item.productType : 'Generic',
  };

  return (
    <StyledConfig
      key={`${item.unitId}${index.toString()}`}
      data-testid={`config-wrapper-${item.unitId}`}
    >
      <StyledStatsContainer>
        <Flex direction="column" gap={16} padding={8}>
          <div>
            <Flex align="center">
              <T h3>{item.companyName}</T>
              <Button
                mode="transparent"
                onClick={() => {
                  if (!navigator.onLine) {
                    return toast({
                      subtitle: intl.formatMessage({
                        defaultMessage:
                          'Please connect to the internet to edit the name of this configuration.',
                        id: 'WmVOTK',
                      }),
                      mode: TOASTTYPE.No_Connection,
                    });
                  }
                  setIsEditModalOpen(true);
                  setSelectedUnitId(item.unitId);
                }}
              >
                <Tooltip title="Change name">
                  <IconEdit />
                </Tooltip>
              </Button>
            </Flex>
            <StyledSubText>
              {!item.productType || item.productType === 'Generic'
                ? 'Standard'
                : item.productType}
            </StyledSubText>
          </div>
          <DisplayStatus {...DisplayStatusProps} />
        </Flex>
        <StyledDivider />
        <Flex direction="column" justify="space-between" gap={16} padding={8}>
          <Flex gap={8} justify="space-between">
            <StyledSubText>
              <FormattedMessage defaultMessage="Last Changed" id="j61EhU" />:
            </StyledSubText>
            <StyledSubText>
              {item.changedAt ? formatDateTime(item.changedAt) : 'unkown'}
            </StyledSubText>
          </Flex>
          <Flex gap={8} justify="space-between">
            <StyledSubText>
              <FormattedMessage defaultMessage="Last Modifier" id="mdJR6m" />:
            </StyledSubText>
            <StyledSubText>{item.changedBy || 'unkown'}</StyledSubText>
          </Flex>
          <Flex gap={8} justify="space-between">
            <StyledSubText>
              <FormattedMessage defaultMessage="Slide Count" id="Jb+SQO" />:
            </StyledSubText>
            <StyledSubText>{item.slideCount || '0'}</StyledSubText>
          </Flex>
        </Flex>
      </StyledStatsContainer>

      <StyledButtonGroup>
        <Button
          size="large"
          mode="ghost"
          testId={`edit-config-button-${item.unitId}`}
          onClick={() => {
            if (!isReadyForOffline && !navigator.onLine) {
              toast({
                subtitle: intl.formatMessage({
                  defaultMessage:
                    'Please connect to the internet to edit the configuration.',
                  id: 'q5NsnT',
                }),
                mode: TOASTTYPE.No_Connection,
              });
              return;
            }
            navigate(`/upload/${item.unitId}`);
          }}
        >
          <Flex justify="center" align="center" gap={8}>
            <span style={{ transform: 'scale(1.5)' }}>
              <IconEdit />
            </span>
            <T>
              <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
            </T>
          </Flex>
        </Button>

        {envIsElectron ? (
          <Button
            size="large"
            onClick={() => {
              if (!navigator.onLine && !isReadyForOffline) {
                toast({
                  title: intl.formatMessage({
                    defaultMessage:
                      'Failed to start player, not all assets have been downloaded for this player.',
                    id: 'zLTGcq',
                  }),
                  subtitle: intl.formatMessage({
                    defaultMessage:
                      'Please go online to download the missing assets.',
                    id: 'LGhXHL',
                  }),
                  mode: TOASTTYPE.Danger,
                  time: DURATIONS.Long,
                });
                return;
              }
              dispatchStartPlayer(item.unitId);
              navigate(`/player/${item.unitId}`);
            }}
          >
            <Flex gap={8} align="center" justify="center">
              <IconPlay />
              <T>
                <FormattedMessage defaultMessage="Start Player" id="flKWqK" />
              </T>
            </Flex>
          </Button>
        ) : null}
      </StyledButtonGroup>
      <div
        style={{
          position: 'absolute',
          zIndex: '10',
          top: '32px',
          right: '32px',
        }}
      >
        <KebapMenu testId={`config-card-kebab-${item.unitId}`}>
          <MenuItem
            testId={`duplicate-config-button-${item.unitId}`}
            icon={<IconDuplicate />}
            onClick={() => {
              if (!navigator.onLine) {
                return toast({
                  mode: TOASTTYPE.No_Connection,
                  subtitle: intl.formatMessage({
                    defaultMessage:
                      'Please connect to the internet to copy the configuration.',
                    id: 'skG64O',
                  }),
                });
              }
              setDuplicateModalOpen(true);
              setSelectedUnitId(item.unitId);
            }}
          >
            Duplicate
          </MenuItem>
          {isAdmin && (
            <MenuItem
              icon={<IconTrash />}
              onClick={() => {
                if (!navigator.onLine) {
                  return toast({
                    mode: TOASTTYPE.No_Connection,
                    subtitle: intl.formatMessage({
                      defaultMessage:
                        'Please connect to the internet to delete the configuration.',
                      id: 'G/1Z36',
                    }),
                  });
                }

                setIsDeleteConfirmationModalOpen(true);
                setSelectedUnitId(item.unitId);
              }}
            >
              Delete
            </MenuItem>
          )}
          {isAdmin && (
            <MenuItem
              icon={<IconPreview />}
              onClick={() => {
                if (!navigator.onLine) {
                  toast({
                    mode: TOASTTYPE.No_Connection,
                    subtitle: intl.formatMessage({
                      defaultMessage:
                        'Please connect to the internet to use the Admin Edit feature.',
                      id: 'vVCgLK',
                    }),
                  });
                  return;
                }

                navigate(`/admin/edit/${item.unitId}`, {
                  state: {
                    name: item?.companyName,
                    hasStatus: Boolean(item.shelves.length),
                    onlineStatus:
                      item.playerTotals.online === item.playerTotals.total ||
                      item.playerTotals.online > 0
                        ? 'Online'
                        : 'Offline',
                  },
                });
              }}
            >
              AdminPanel
            </MenuItem>
          )}
        </KebapMenu>
      </div>
    </StyledConfig>
  );
};

export default ConfigCard;
